/**
 * @fileoverview Input/output schemas for inspiration-attribute-deriver-v1.
 *
 * This agent analyzes a photo of a person and derives PersonProfileSchema attributes
 * based on what is visually observable, augmented by the model's knowledge of the
 * named subject. The image mode (portrait, half-body, full-body) determines which
 * attributes the agent should attempt to derive.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import {
  SHAPE_DEFINITIONS,
  ATHLETICISM_DEFINITIONS,
  SKINTONE_DEFINITIONS,
  CHEST_DEFINITIONS,
  WAIST_DEFINITIONS,
  HIPS_DEFINITIONS,
  EYE_COLOR_OPTIONS,
  HAIR_COLOR_OPTIONS,
  BODY_ARCHETYPE_NAMES,
  ETHNICITY_L1_NAMES,
} from '@/domains/characters/constants';

// =============================================================================
// INPUT SCHEMAS
// =============================================================================

export const ImageModeSchema = z.enum(['portrait', 'half-body', 'full-body']);

/**
 * Simplified input from UI hooks.
 */
export const HookInputSchema = z.object({
  imageDataUris: z.array(z.string()).min(1).max(4).describe('Array of image data URIs (base64 encoded) to analyze. 1-4 images of the same person.'),
  imageMode: ImageModeSchema.describe('The best (most comprehensive) framing mode among the selected images. Determines which attributes to derive.'),
  inspirationName: z.string().describe('The name of the inspiration/person in the image. Used for knowledge augmentation.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

/**
 * Full input for the agent flow - extends base schema.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  imageDataUris: z.array(z.string()).min(1).max(4).describe(
    'Array of image data URIs (base64 encoded) to analyze. 1-4 images of the same person.'
  ),
  imageMode: ImageModeSchema.describe(
    'The best (most comprehensive) framing mode among the selected images. Determines which attributes to derive.'
  ),
  inspirationName: z.string().describe(
    'The name of the inspiration/person in the image. Used for knowledge augmentation and sanity-checking derived attributes.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for derived inspiration attributes.
 * All fields are optional because derivability depends on image mode.
 * Only fields that are actually observable in the image should be populated.
 *
 * Uses enum constraints from the canonical constants to ensure derived values
 * are valid PersonProfileSchema values.
 *
 * faceDescription and bodyDescription are detailed prose paragraphs (not arrays)
 * suitable for text-to-image model prompting.
 */
export const DerivedAttributesSchema = z.object({
  // Portrait-level attributes (derivable from all modes)
  age: z.number().optional().describe('Estimated age in years.'),
  gender: z.enum(['Female', 'Male', 'Non-binary']).optional().describe('Apparent gender: Female, Male, or Non-binary.'),
  skinTone: z.enum(Object.keys(SKINTONE_DEFINITIONS) as [string, ...string[]]).optional().describe('Skin tone classification.'),
  eyeColor: z.enum(EYE_COLOR_OPTIONS as unknown as [string, ...string[]]).optional().describe('Eye color.'),
  hairColor: z.enum(HAIR_COLOR_OPTIONS as unknown as [string, ...string[]]).optional().describe('Hair color.'),
  faceDescription: z.string().optional().describe('Detailed prose paragraph describing facial features and hair, suitable for text-to-image generation.'),
  ethnicities: z.record(z.enum(ETHNICITY_L1_NAMES), z.array(z.string())).optional().describe('Apparent ethnic background(s) as record of L1 group to L2 sub-ethnicities. Keys MUST be from the official L1 list.'),

  // Half-body attributes (derivable from half-body and full-body)
  shape: z.enum(Object.keys(SHAPE_DEFINITIONS) as [string, ...string[]]).optional().describe('Body shape classification.'),
  athleticism: z.enum(Object.keys(ATHLETICISM_DEFINITIONS) as [string, ...string[]]).optional().describe('Athleticism level.'),
  chest: z.enum(Object.keys(CHEST_DEFINITIONS) as [string, ...string[]]).optional().describe('Chest width classification.'),
  bodyDescription: z.string().optional().describe('Detailed prose paragraph describing body and physique, suitable for text-to-image generation.'),

  // Full-body attributes (derivable only from full-body)
  waist: z.enum(Object.keys(WAIST_DEFINITIONS) as [string, ...string[]]).optional().describe('Waist width classification.'),
  hips: z.enum(Object.keys(HIPS_DEFINITIONS) as [string, ...string[]]).optional().describe('Hip width classification.'),
  height: z.number().optional().describe('Estimated height in cm.'),
  bodyArchetype: z.enum(BODY_ARCHETYPE_NAMES as unknown as [string, ...string[]]).optional().describe('Body archetype classification.'),
});

export type DerivedAttributes = z.infer<typeof DerivedAttributesSchema>;

export const OutputSchema = z.object({
  isValid: z.boolean().describe(
    'True if the image contains exactly one person and can be analyzed. False otherwise.'
  ),
  error: z.string().optional().describe(
    'Explanation of why the image is invalid, if isValid is false.'
  ),
  attributes: DerivedAttributesSchema.optional().describe(
    'The derived attributes. Only present if isValid is true.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
